import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSafeBack } from "../hooks/useSafeBack";
import { ArrowLeft, Send, LifeBuoy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

export default function SupportThread() {
  const navigate = useNavigate();
  const goBack = useSafeBack("/settings");
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const listRef = useRef();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/support/inbox");
      setMsgs(data.messages || []);
      if (data.unread > 0) { await api.post("/support/read").catch(() => {}); window.dispatchEvent(new Event("fv-refresh-badge")); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    document.body.classList.add("hide-bottom-nav");
    return () => document.body.classList.remove("hide-bottom-nav");
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, [msgs]);

  const send = async () => {
    if (!text.trim()) return;
    const body = text.trim();
    setText("");
    try {
      await api.post("/support/message", { text: body });
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="flex flex-col" style={{ height: "100dvh" }}>
      <div className="sticky top-0 z-40 glass hairline px-4 py-3 flex items-center gap-3">
        <button onClick={goBack} data-testid="support-back-btn"><ArrowLeft size={22} /></button>
        <div className="h-9 w-9 rounded-full grid place-items-center" style={{ background: "color-mix(in srgb, var(--accent) 16%, transparent)" }}>
          <LifeBuoy size={17} className="gold-text" />
        </div>
        <h1 className="font-semibold text-base truncate">Vault Support</h1>
      </div>
      <div ref={listRef} className="flex-1 overflow-y-auto no-scrollbar px-4 py-4 space-y-2" data-testid="support-thread-messages">
        {loading ? (
          <div className="grid place-items-center py-20"><Loader2 className="animate-spin gold-text" /></div>
        ) : msgs.length === 0 ? (
          <p className="text-center text-sm py-10" style={{ color: "var(--text-muted)" }}>
            Send us a message and our team will reply here.
          </p>
        ) : msgs.map((m) => {
          const mine = m.direction === "to_support";
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`support-msg-${m.id}`}>
              <div className="max-w-[78%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap"
                style={mine ? { background: "var(--accent)", color: "#fff" } : { background: "var(--overlay)", color: "var(--text-primary)", border: "1px solid var(--border)" }}>
                {!mine && <div className="text-[10px] gold-text mb-0.5">Vault Support</div>}
                {m.text}
              </div>
            </div>
          );
        })}
      </div>
      <div className="glass p-3 flex items-center gap-2" style={{ paddingBottom: "calc(env(safe-area-inset-bottom,0px) + 12px)" }}>
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Message support…" data-testid="support-thread-input"
          className="flex-1 rounded-full px-4 py-3 text-sm outline-none"
          style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)", fontSize: 16 }} />
        <button onClick={send} data-testid="support-thread-send" className="h-11 w-11 rounded-full grid place-items-center shrink-0" style={{ background: "var(--accent)", color: "#fff" }}>
          <Send size={18} />
        </button>
      </div>
    </div>
  );
}
